/**
 * ==========================================================================
 *  TwoFaEinrichtung.jsx — Dialog zum Ein-/Ausschalten der 2FA
 * ==========================================================================
 *  Zeigt je nach Zustand:
 *   - 2FA aus  -> Einrichtungs-Assistent (QR scannen + Code bestaetigen)
 *   - 2FA an   -> Info + Deaktivieren (mit Passwort-Bestaetigung)
 *
 *  Backend:
 *   POST /api/auth/2fa/setup     -> { secret, qr }   (legt Schluessel an, noch nicht scharf)
 *   POST /api/auth/2fa/activate  -> { status }       (Code pruefen, scharf schalten)
 *   POST /api/auth/2fa/disable   -> { status }       (Passwort pruefen, abschalten)
 *
 *  onChanged() wird nach Ein-/Ausschalten aufgerufen (laedt den User neu,
 *  damit das UI den neuen Status kennt).
 * ==========================================================================
 */

import { useState } from 'react';
import { X, ShieldCheck, ShieldOff, Loader2, AlertCircle, CheckCircle2, Smartphone } from 'lucide-react';
import { api, ApiError } from '../api';

const CI = { brombeer: '#85366C' };

export default function TwoFaEinrichtung({ user, onClose, onChanged }) {
  const aktiv = !!user?.twofa_aktiviert;

  // Einrichtungs-Flow
  const [setup, setSetup] = useState(null);      // { secret, qr } nach /setup
  const [code, setCode] = useState('');
  const [passwort, setPasswort] = useState('');
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState('');
  const [fertig, setFertig] = useState(false);   // Erfolgs-Screen

  async function starteEinrichtung() {
    setFehler(''); setBusy(true);
    try {
      const r = await api.post('/api/auth/2fa/setup');
      setSetup(r);
    } catch (e) {
      setFehler(e instanceof ApiError ? (e.detail || 'Einrichtung fehlgeschlagen.') : 'Server nicht erreichbar.');
    } finally { setBusy(false); }
  }

  async function aktiviere() {
    setFehler(''); setBusy(true);
    try {
      await api.post('/api/auth/2fa/activate', { code: code.trim() });
      setFertig(true);
      if (onChanged) await onChanged();
    } catch (e) {
      setFehler(e instanceof ApiError ? (e.detail || 'Code falsch.') : 'Server nicht erreichbar.');
    } finally { setBusy(false); }
  }

  async function deaktiviere() {
    setFehler(''); setBusy(true);
    try {
      await api.post('/api/auth/2fa/disable', { passwort });
      if (onChanged) await onChanged();
      onClose();
    } catch (e) {
      setFehler(e instanceof ApiError ? (e.detail || 'Passwort falsch.') : 'Server nicht erreichbar.');
    } finally { setBusy(false); }
  }

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center z-[1000] p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-gray-100 bg-slate-50 flex items-center justify-between">
          <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
            <ShieldCheck className="w-5 h-5" style={{ color: CI.brombeer }} />
            Zwei-Faktor-Anmeldung
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 p-1.5 rounded-md hover:bg-gray-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-6 space-y-4">
          {/* ---------- Fall 1: 2FA ist bereits aktiv ---------- */}
          {aktiv && (
            <>
              <div className="flex items-start gap-2 p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-sm text-emerald-800">
                <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Zwei-Faktor-Anmeldung ist für dein Konto <strong>aktiv</strong>. Beim Login wird nach dem Passwort dein App-Code verlangt.</span>
              </div>
              <div className="pt-2 border-t border-gray-100">
                <p className="text-sm text-gray-600 mb-2">Zum Abschalten dein aktuelles Passwort eingeben:</p>
                <input
                  type="password"
                  autoComplete="current-password"
                  value={passwort}
                  onChange={(e) => setPasswort(e.target.value)}
                  placeholder="Aktuelles Passwort"
                  className="w-full py-2.5 px-3 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2"
                  style={{ '--tw-ring-color': CI.brombeer }}
                />
                {fehler && (
                  <div className="flex items-start gap-2 mt-3 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                    <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{fehler}</span>
                  </div>
                )}
                <button
                  onClick={deaktiviere}
                  disabled={busy || !passwort}
                  className="mt-3 w-full py-2.5 rounded-lg font-bold text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldOff className="w-4 h-4" />}
                  Zwei-Faktor deaktivieren
                </button>
              </div>
            </>
          )}

          {/* ---------- Fall 2: Einrichtung, Erfolg ---------- */}
          {!aktiv && fertig && (
            <div className="text-center py-4">
              <div className="w-14 h-14 rounded-full bg-emerald-100 mx-auto mb-4 flex items-center justify-center">
                <CheckCircle2 className="w-7 h-7 text-emerald-600" />
              </div>
              <h4 className="text-lg font-bold text-gray-800 mb-1">Aktiviert!</h4>
              <p className="text-sm text-gray-600 mb-5">
                Ab dem nächsten Login brauchst du zusätzlich den Code aus deiner Authenticator-App.
              </p>
              <button onClick={onClose} className="py-2.5 px-6 rounded-lg font-bold text-white" style={{ backgroundColor: CI.brombeer }}>
                Fertig
              </button>
            </div>
          )}

          {/* ---------- Fall 3: Einrichtung, Schritt 0 (Start) ---------- */}
          {!aktiv && !fertig && !setup && (
            <>
              <div className="flex items-start gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
                <Smartphone className="w-4 h-4 mt-0.5 shrink-0" />
                <span>Du brauchst eine Authenticator-App auf dem Handy (z.B. Google Authenticator). Danach zeigen wir dir einen QR-Code zum Scannen.</span>
              </div>
              {fehler && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{fehler}</span>
                </div>
              )}
              <button
                onClick={starteEinrichtung}
                disabled={busy}
                className="w-full py-2.5 rounded-lg font-bold text-white disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: CI.brombeer }}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Einrichtung starten
              </button>
            </>
          )}

          {/* ---------- Fall 4: Einrichtung, Schritt 1 (QR + Code) ---------- */}
          {!aktiv && !fertig && setup && (
            <>
              <p className="text-sm text-gray-600">
                <strong>1.</strong> Scanne diesen QR-Code in deiner Authenticator-App:
              </p>
              <div className="flex justify-center">
                <img src={setup.qr} alt="QR-Code für Authenticator-App" className="w-44 h-44 border border-gray-200 rounded-lg p-2 bg-white" />
              </div>
              <details className="text-xs text-gray-500">
                <summary className="cursor-pointer">QR lässt sich nicht scannen? Schlüssel manuell eingeben</summary>
                <code className="block mt-2 p-2 bg-gray-50 border border-gray-200 rounded break-all select-all">{setup.secret}</code>
              </details>
              <p className="text-sm text-gray-600 pt-1">
                <strong>2.</strong> Gib den aktuell angezeigten 6-stelligen Code ein:
              </p>
              <input
                type="text"
                inputMode="numeric"
                autoFocus
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                placeholder="123456"
                className="w-full py-2.5 text-center tracking-[0.4em] text-lg font-bold border border-gray-300 rounded-lg focus:outline-none focus:ring-2"
                style={{ '--tw-ring-color': CI.brombeer }}
              />
              {fehler && (
                <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                  <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" /><span>{fehler}</span>
                </div>
              )}
              <button
                onClick={aktiviere}
                disabled={busy || code.length < 6}
                className="w-full py-2.5 rounded-lg font-bold text-white disabled:opacity-50 flex items-center justify-center gap-2"
                style={{ backgroundColor: CI.brombeer }}
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}
                Aktivieren
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
